import { useState, useEffect, useCallback, useRef } from 'react';
import { sendFeedback } from '../../../apis/ia';
import { useWebgate } from '@xvia/webgate-connect';

const INACTIVITY_TIMEOUT = 40000;

const FAREWELL_PATTERNS = [
  /\b(tchau|adeus|até logo|até mais|até breve|até a próxima|até depois)\b/i,
  /\b(obrigad[oa]|valeu|vlw)\b/i,
  /\b(bye|goodbye|thanks|thank you)\b/i,
  /\b(encerrar|finalizar|terminar)\b/i,
  /\b(despedida|despedir)\b/i,
  /\b(agradecimento|agradecer)\b/i,
];

export const useNPSFeedback = (conversationId: string | undefined, scrollToBottom?: (force?: boolean) => void) => {
  const [showNPS, setShowNPS] = useState(false);
  const [hasSubmittedFeedback, setHasSubmittedFeedback] = useState(false);
  const [hasUserSentMessage, setHasUserSentMessage] = useState(false);
  const inactivityTimerRef = useRef<ReturnType<typeof setTimeout>>();
  const lastActivityRef = useRef<number>(Date.now());
    const { accessToken, userInfo } = useWebgate();


  const checkForFarewell = useCallback((message: string) => {
    return FAREWELL_PATTERNS.some(pattern => pattern.test(message));
  }, []);

  const resetInactivityTimer = useCallback(() => {
    if (inactivityTimerRef.current) {
      clearTimeout(inactivityTimerRef.current);
    }

    if (hasUserSentMessage && !hasSubmittedFeedback) {
      inactivityTimerRef.current = setTimeout(() => {
        setShowNPS(true);
        if (scrollToBottom) {
          scrollToBottom(true);
        }
      }, INACTIVITY_TIMEOUT);
    }
  }, [hasSubmittedFeedback, hasUserSentMessage, scrollToBottom]);

  const handleUserActivity = useCallback(() => {
    lastActivityRef.current = Date.now();
    setShowNPS(false);
    resetInactivityTimer();
  }, [resetInactivityTimer]);

  const handleFeedback = useCallback(async (rating: number) => {
    if (!conversationId) {
      throw new Error('ID da conversa não encontrado');
    }

    try {
      await sendFeedback(conversationId, rating, accessToken, userInfo);
      setHasSubmittedFeedback(true);
      setShowNPS(false);
    } catch (error) {
      console.error('Erro ao enviar feedback:', error);
      throw error;
    }
  }, [conversationId]);

  const handleDismiss = useCallback(() => {
    setShowNPS(false);
    resetInactivityTimer();
  }, [resetInactivityTimer]);

  const markUserMessageSent = useCallback((message?: string) => {
    setHasUserSentMessage(true);
    
    if (message && checkForFarewell(message)) {
      setShowNPS(true);
      if (scrollToBottom) {
        scrollToBottom(true);
      }
      if (inactivityTimerRef.current) {
        clearTimeout(inactivityTimerRef.current);
      }
    } else {
      resetInactivityTimer();
    }
  }, [resetInactivityTimer, checkForFarewell, scrollToBottom]);

  useEffect(() => {
    resetInactivityTimer();
    return () => {
      if (inactivityTimerRef.current) {
        clearTimeout(inactivityTimerRef.current);
      }
    };
  }, [resetInactivityTimer]);

  return {
    showNPS,
    handleFeedback,
    handleDismiss,
    handleUserActivity,
    markUserMessageSent,
  };
}; 